import React, { useEffect, useState } from 'react';
import { useWhiteLabel } from '@/contexts/WhiteLabelContext';
import { isFeatureEnabled } from '@/utils/featuresUtil';
import { Palette, X, RefreshCw, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export function WhiteLabelPicker() {
  const [isOpen, setIsOpen] = useState(false);
  const { 
    config, 
    allConfigs, 
    loadAllConfigs, 
    overrideDomain, 
    setOverride,
    isLoading 
  } = useWhiteLabel();

  // Only show in development if feature is enabled
  if (!isFeatureEnabled('White Label Picker')) {
    return null;
  }

  const handleOpen = async () => {
    setIsOpen(true);
    await loadAllConfigs();
  };

  const handleSelect = (domain: string | null) => {
    setOverride(domain);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          onClick={handleOpen}
          className="gap-2 bg-yellow-100 hover:bg-yellow-200 border-yellow-400 text-yellow-800"
        >
          <Palette className="h-4 w-4" />
          <span className="hidden sm:inline">
            {overrideDomain ? `WL: ${overrideDomain}` : 'White Label'}
          </span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            White Label Picker
          </DialogTitle>
          <DialogDescription>
            Select a white-label configuration to preview. This only works in development.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Current selection */}
          {overrideDomain && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10 border border-primary/20">
              <div>
                <p className="text-sm font-medium">Currently previewing:</p>
                <p className="text-xs text-muted-foreground">{overrideDomain}</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => handleSelect(null)}
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          )}

          {/* Config list */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Available Configurations</p>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={loadAllConfigs}
                disabled={isLoading}
              >
                <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
              </Button>
            </div>

            {allConfigs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">No white-label configurations found.</p>
                <p className="text-xs mt-1">Create one in Flight Control → Tools → White Label</p>
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto space-y-1">
                {/* Default option */}
                <button
                  onClick={() => handleSelect(null)}
                  className={cn(
                    "w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors",
                    !overrideDomain 
                      ? "border-primary bg-primary/5" 
                      : "border-transparent hover:bg-muted"
                  )}
                >
                  <div className="w-8 h-8 rounded bg-gray-400 flex items-center justify-center text-white text-xs font-bold">
                    D
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Default (Current Domain)</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {window.location.hostname}
                    </p>
                  </div>
                  {!overrideDomain && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </button>

                {/* Config options */}
                {allConfigs.map((cfg) => (
                  <button
                    key={cfg.id}
                    onClick={() => handleSelect(cfg.domain)}
                    className={cn(
                      "w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors",
                      overrideDomain === cfg.domain 
                        ? "border-primary bg-primary/5" 
                        : "border-transparent hover:bg-muted"
                    )}
                  >
                    {cfg.logoLight ? (
                      <img 
                        src={cfg.logoLight} 
                        alt={cfg.appName}
                        className="w-8 h-8 rounded object-contain"
                      />
                    ) : (
                      <div 
                        className="w-8 h-8 rounded flex items-center justify-center text-white text-xs font-bold"
                        style={{ backgroundColor: cfg.primaryColor }}
                      >
                        {cfg.appName.charAt(0)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{cfg.appName}</p>
                      <p className="text-xs text-muted-foreground truncate">{cfg.domain}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Color swatches */}
                      <div className="flex gap-0.5">
                        <div 
                          className="w-3 h-3 rounded-full border"
                          style={{ backgroundColor: cfg.primaryColor }}
                        />
                        <div 
                          className="w-3 h-3 rounded-full border"
                          style={{ backgroundColor: cfg.sidebarBackground }}
                        />
                      </div>
                      {overrideDomain === cfg.domain && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
